---
title: "Pricing — $4 per user, per month"
description: "Flat $4/user/month for the CRM with call recording auto-link, WhatsApp and cold email. Add AI Call Analysis for $5/user/month. Free for up to 5 users, with 100 recordings, 100 WhatsApp messages and 300 outreach emails to try."
seoDescription: "NerdCRM is $4/user/month with call recording, WhatsApp and cold email included. AI Call Analysis is +$5/user. Free for up to 5 users."
slug: "main"
updatedAt: "2026-10-22"
heroAnswer: "NerdCRM costs $4 per user per month. Call recording, WhatsApp outreach, cold email, KAM reports and integrations are all included. AI Call Analysis, which transcribes and scores every recorded call, is an optional add-on at $5 per user per month. The free plan covers up to 5 users and includes 100 call recordings, 100 WhatsApp messages and 300 outreach emails to try before you pay."
faq:
  - question: "What does the AI Call Analysis add-on cost?"
    answer: "$5 per user per month on top of the $4 plan. It includes 300 analysed calls per user per month, shared across your team. If you need more, extra calls cost $3 per 100, only up to a monthly limit the owner sets (or none at all). Paid plans without the add-on get a taster of 20 analysed calls per user per month."
  - question: "Can I add or remove AI Call Analysis any time?"
    answer: "Yes, from Settings → Billing. Adding it charges the prorated amount for the rest of the month; removing it credits the unused part. Extra calls already used in the month are still billed."
  - question: "Do I need a credit card to start?"
    answer: "No. NerdCRM is free for up to 5 users, and the free plan includes 100 call recordings, 100 WhatsApp messages and 300 outreach emails so you can try those features before paying. You can also use the public read-only demo at /demo with no signup at all."
  - question: "What happens when I use up the free recordings, messages or emails?"
    answer: "That feature pauses until you subscribe — nothing is deleted. Your existing recordings and conversations stay where they are, and everything resumes the moment you upgrade. The allowance is one-time per organization, not monthly. The rest of the CRM stays free for up to 5 users."
  - question: "How long are call recordings kept?"
    answer: "45 days from the call. Recordings are stored privately in the cloud (encrypted at rest) and deleted automatically after that. The AI analysis, transcript summary and lead recommendation stay on the contact after the audio is gone."
  - question: "How does payment work?"
    answer: "Billing is monthly via Paddle, for every active user. Adding a user or the AI add-on mid-month is charged prorated right away; removing one credits the unused part. Cancel anytime from the billing settings page."
---

# Pricing

## $4 per user, per month

NerdCRM is built to be the cheapest option that still has the features small phone-heavy sales teams need. Every active user is billed at the same rate.

## What's included at every paid seat

- Auto-linked call recordings from MicroSIP, kept 45 days
- Every dial logged, answered or not, on a per-person daily timeline
- WhatsApp template campaigns and live inbox on your own Meta number
- Cold email campaigns with a unified reply inbox
- Kanban pipeline management (drag-and-drop deals across stages)
- KAM, sales, Deal Cadence and Smart Reports
- Payment tracking and due-payment management
- CSV import/export, Facebook Ads import, webhook lead capture, API keys
- AI taster: 20 analysed calls per user per month

## AI Call Analysis add-on: +$5 per user, per month

Every recorded call is transcribed (Bangla and English) and scored by AI on fit, need, decision maker, budget, timing and engagement. Each lead gets a recommendation on the contact and the deal: qualified, nurture, needs another call or disqualified.

- 300 analysed calls per user per month, shared across the team
- Extra calls $3 per 100, added to your next invoice, only up to the monthly limit you set
- Add or remove it any time from Settings → Billing (prorated)

See [how AI Call Analysis works](/features/ai-call-analysis).

## Free tier

Free for up to 5 users — no credit card required.

Call recording, WhatsApp outreach and cold email are paid features, and the free plan includes a one-time allowance of **100 call recordings, 100 WhatsApp messages and 300 outreach emails** so you can try them properly before deciding. When an allowance runs out that feature pauses until you subscribe — nothing is deleted, and everything picks up where it left off when you upgrade. The rest of the CRM stays free.

## Cancel anytime

Subscriptions are month-to-month. You can cancel from billing settings and you keep access until the end of the paid period.
